// Manifiesto de los assets FOTOGRÁFICOS de la pizza (public/pizza-fotos/).
//
// Generados con Magnific (Seedream 5 Pro) el 2026-08-03: todos cenitales, con la
// misma luz suave desde arriba-izquierda y recortados a PNG transparente, para que
// compongan por capas como un solo plato.
//
// 🔴 TODAVÍA NO ESTÁ CABLEADO AL DIBUJO. `PizzaCanvas` sigue siendo vectorial; este
// manifiesto existe para que el compositor fotográfico (siguiente paso) resuelva
// assets por nombre de ingrediente con LA MISMA normalización del catálogo vectorial
// (`normalizar` de pizzaIngredients) — un ingrediente sin foto cae al dibujo de
// siempre, nunca a un hueco.

import { normalizar } from "@/lib/menu/pizzaIngredients"
import type { NivelQueso, Salsa } from "@/lib/menu/pizzaBase"

const RUTA = "/pizza-fotos"

/**
 * Base = corteza + salsa + (queso fundido o no). Una imagen por combinación real:
 * el queso TAPA la salsa, así que no se pueden componer como capas separadas — el
 * anillo del borde es distinto en cada combinación.
 */
export function fotoBase(salsa: Salsa, queso: NivelQueso): string {
  const conQueso = queso !== "sin"
  const porSalsa: Record<Salsa, string> = conQueso
    ? {
        roja: "base-roja-queso.png",
        blanca: "base-blanca-queso.png",
        bbq: "base-bbq-queso.png",
        ninguna: "base-sinsalsa-queso.png",
      }
    : {
        roja: "base-roja-sinqueso.png",
        blanca: "base-blanca-sinqueso.png",
        bbq: "base-bbq-sinqueso.png",
        ninguna: "base-masa-sola.png",
      }
  return `${RUTA}/${porSalsa[salsa]}`
}

/** Patrones → archivo de pieza. Mismo espíritu que el CATALOGO vectorial: el orden
 *  importa y las palabras cortas van ancladas. */
const FOTOS_TOPPING: Array<{ re: RegExp; archivo: string }> = [
  { re: /pepperoni|peperoni|salami|salame/, archivo: "topping-pepperoni.png" },
  { re: /champin|hongo|\bseta\b|portobello/, archivo: "topping-champinon-lamina.png" },
  { re: /aceitun|oliva|olive/, archivo: "topping-aceituna.png" },
  { re: /jamon|prosciut/, archivo: "topping-jamon.png" },
  { re: /tocin|bacon|panceta/, archivo: "topping-tocineta.png" },
  { re: /pollo|pechuga/, archivo: "topping-pollo.png" },
  { re: /cebolla|onion/, archivo: "topping-cebolla.png" },
  { re: /pimen|piment|morron|bell/, archivo: "topping-pimenton.png" },
  { re: /jalapen|\bchile\b/, archivo: "topping-jalapeno.png" },
  { re: /\bpina\b|ananas|pineapple/, archivo: "topping-pina.png" },
  { re: /albahaca|basil/, archivo: "topping-albahaca.png" },
]

/** Foto de la pieza del ingrediente, o `null` para caer al dibujo vectorial. */
export function fotoTopping(nombre: string): string | null {
  const n = normalizar(nombre)
  for (const { re, archivo } of FOTOS_TOPPING) {
    if (re.test(n)) return `${RUTA}/${archivo}`
  }
  return null
}

/**
 * Cuánto mide la pieza, como FRACCIÓN DEL DIÁMETRO de la pizza.
 *
 * 🔴 Por qué existe, y por qué NO se reusa `escala` del catálogo vectorial: el número
 * del catálogo es un multiplicador relativo, afinado para formas dibujadas. Aplicado a
 * las fotos daba un jamón de 84 unidades sobre una pizza de 236 — el 36 % del plato.
 * Seis de esos y la pizza no se veía; era una bandeja de fiambre.
 *
 * Acá el número es una MEDIDA: sobre una pizza mediana de 30 cm, un pepperoni mide
 * ~4,5 cm ⇒ 0,15. Se puede discutir cada valor mirando una pizza de verdad, que es
 * exactamente lo que se busca.
 *
 * Depende de que las fotos estén NORMALIZADAS al contenido (recortadas a su caja
 * alfa): si una imagen trae aire alrededor, la pieza se dibuja más chica que su medida
 * sin que nadie entienda por qué. Antes ocupaban entre el 34 % y el 77 % de su marco,
 * y esa sola dispersión ya hacía que el conjunto se viera desparejo.
 */
const TAMANO_FOTO: Array<{ re: RegExp; fraccion: number }> = [
  { re: /pepperoni|peperoni|salami|salame/, fraccion: 0.15 },
  { re: /jamon|prosciut/, fraccion: 0.13 },
  { re: /pimen|piment|morron|bell/, fraccion: 0.15 },
  { re: /albahaca|basil/, fraccion: 0.12 },
  { re: /cebolla|onion/, fraccion: 0.13 },
  { re: /champin|hongo|\bseta\b|portobello/, fraccion: 0.115 },
  { re: /\bpina\b|ananas|pineapple/, fraccion: 0.1 },
  { re: /tocin|bacon|panceta/, fraccion: 0.095 },
  { re: /pollo|pechuga/, fraccion: 0.09 },
  { re: /jalapen|\bchile\b/, fraccion: 0.075 },
  { re: /aceitun|oliva|olive/, fraccion: 0.07 },
]

/**
 * Medida por defecto para lo que no está en la tabla — un ingrediente propio de la
 * pizzería que cayó al dibujo vectorial.
 *
 * El valor NO coincide con ninguna entrada de la tabla, a propósito: así el test puede
 * distinguir "no está en la tabla" de "mide justo eso", y un ingrediente con foto que
 * se quede sin medida se cae solo en vez de salir de un tamaño plausible pero mudo.
 */
export const TAMANO_POR_DEFECTO = 0.11

/** Fracción del diámetro que mide la pieza. */
export function tamanoFoto(nombre: string): number {
  const n = normalizar(nombre)
  for (const { re, fraccion } of TAMANO_FOTO) {
    if (re.test(n)) return fraccion
  }
  return TAMANO_POR_DEFECTO
}

/**
 * Dónde termina la salsa y empieza la corteza, como fracción del radio.
 *
 * Los ingredientes van sobre la salsa, NUNCA sobre el borde: una pizza con piña
 * encaramada en la corteza —o colgando fuera del plato, que es lo que estaba
 * pasando— se lee como un error de dibujo antes que como una pizza.
 */
export const RADIO_SALSA = 0.8

/** Todos los archivos del manifiesto, para el test que vigila que existan. */
export const TODOS_LOS_ARCHIVOS: readonly string[] = [
  "base-roja-queso.png", "base-blanca-queso.png", "base-bbq-queso.png",
  "base-sinsalsa-queso.png", "base-roja-sinqueso.png", "base-blanca-sinqueso.png",
  "base-bbq-sinqueso.png", "base-masa-sola.png",
  "topping-pepperoni.png", "topping-champinon-entero.png", "topping-champinon-lamina.png",
  "topping-aceituna.png", "topping-jamon.png", "topping-tocineta.png", "topping-pollo.png",
  "topping-cebolla.png", "topping-pimenton.png", "topping-jalapeno.png", "topping-pina.png",
  "topping-albahaca.png",
]
